import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface BackgroundCheckPackage {
  id: string;
  name: string;
  priceUsd: number;
  features: string[];
}

export interface BackgroundCheckPackagesResponse {
  packages: BackgroundCheckPackage[];
}

export interface SearchResultDto {
  searchId: string;
  searchName: string;
  notes: string | null;
  recordsFound: number;
}

export interface BackgroundCheckStatusResponse {
  id: number;
  jobApplicationId: number;
  status: string;
  paymentStatus: string;
  invitationId: string | null;
  orderId: string | null;
  orderPrice: number | null;
  amount: number;
  currency: string;
  createdAt: string;
  completedAt: string | null;
  failureReason: string | null;
  recordsFound: number | null;
  searchResults: SearchResultDto[] | null;
}

export interface InitiateBackgroundCheckRequest {
  jobApplicationId: number;
  packageType: string;
}

export interface InitiateBackgroundCheckResponse {
  isSuccess: boolean;
  backgroundCheckId: number | null;
  clientSecret: string | null;
  errorMessage: string | null;
  existingStatus: string | null;
  autoConfirmed?: boolean;
}

export interface ConfirmBackgroundCheckPaymentResponse {
  isSuccess: boolean;
  errorMessage: string | null;
  status: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundCheckService {
  private readonly apiUrl = `${environment.apiUrl}BackgroundCheck`;

  constructor(private http: HttpClient) {}

  /**
   * Gets available background check packages with pricing
   */
  getPackages(): Observable<BackgroundCheckPackagesResponse> {
    return this.http.get<BackgroundCheckPackagesResponse>(`${this.apiUrl}/packages`);
  }

  /**
   * Gets the background check status for a job application
   */
  getStatus(jobApplicationId: number): Observable<BackgroundCheckStatusResponse | null> {
    return this.http.get<BackgroundCheckStatusResponse | null>(
      `${this.apiUrl}/status/${jobApplicationId}`
    );
  }

  /**
   * Initiates a new background check for a job application
   */
  initiateBackgroundCheck(request: InitiateBackgroundCheckRequest): Observable<InitiateBackgroundCheckResponse> {
    return this.http.post<InitiateBackgroundCheckResponse>(
      `${this.apiUrl}/initiate`,
      request
    );
  }

  /**
   * Confirms that payment has been completed and triggers the background check
   */
  confirmPayment(
    backgroundCheckId: number, 
    paymentIntentId: string
  ): Observable<ConfirmBackgroundCheckPaymentResponse> {
    return this.http.post<ConfirmBackgroundCheckPaymentResponse>(
      `${this.apiUrl}/confirm-payment`,
      { backgroundCheckId, paymentIntentId }
    );
  }
}